import { memo, useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import {
  AnalyticsConstants,
  AnalyticsHooks,
  AnalyticsStyles,
  DemoChip,
  OverviewInsights,
} from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { trackingIds } from '../../tracking-ids';
import { DataSyncInProgress } from '../practice/data-sync-in-progress';
import { useIsPADemoAccount } from '../practice/hooks/use-is-pa-demo-account';
import { usePracticeAnalyticsDemoData } from '../practice/hooks/use-practice-analytics-demo-data';
import { OverviewCardProps } from './types';

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      hygieneReappointment {
        totals(start: $start, end: $end, step: $step) {
          patients
          rescheduledSameDay
          unscheduled
        }
      }
    }
  }
`;

const colors = {
  rescheduledSameDay: theme.colors.success30,
  unscheduled: theme.colors.warning20,
};

const labels = {
  rescheduledSameDay: i18next.t('Reappointment Same Day', { ns: 'analytics' }),
  unscheduled: i18next.t('Not Yet Rescheduled', { ns: 'analytics' }),
  total: i18next.t('Total', { ns: 'analytics' }),
};

export const PracticeAnalyticsOnDashboard = memo(({ filters }: OverviewCardProps) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const isDemoAccount = useIsPADemoAccount();
  const { noLocationsHaveAclAccess } = AnalyticsHooks.useHasAclAccess({
    isDemoAccount,
    permissions: [Permission.ANALYTICS_READ],
  });
  const demoData = usePracticeAnalyticsDemoData();
  const { hasDataSynced, isLoadingLocations, locationIds } = AnalyticsHooks.useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'PA',
  });

  const { data: practiceData, isLoading: isLoadingPracticeData } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `homepage-practice-summary-${isDemoAccount}-${JSON.stringify(filters)}-${locationIds?.join(',')}`
    ),
    queryFn: () =>
      isDemoAccount || !locationIds?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.HygieneFollowUpResponse>({
            locationIds,
            queries: [query],
            variables: {
              end: parseInt(filters?.endDate?.format('YYYYMMDD')),
              start: parseInt(filters?.startDate?.format('YYYYMMDD')),
              step: 'DAILY',
            },
          }),
    onError: () => {
      alert.error(t("Couldn't load the hygiene follow-up data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.hygieneFollowUp : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { rescheduledSameDay, totals, unscheduled } = useMemo(() => {
    const { aggregatedData } = PracticeAnalyticsAggregations.hygieneFollowUpSummary(practiceData) || {};

    return {
      rescheduledSameDay: aggregatedData?.hygieneReappointment?.totals?.rescheduledSameDay || 0,
      totals: aggregatedData?.hygieneReappointment?.totals?.patients || 0,
      unscheduled: aggregatedData?.hygieneReappointment?.totals?.unscheduled || 0,
    };
  }, [practiceData]);

  return (
    <Chart
      bottomContentRenderer={<OverviewInsights title={t('Total Hygiene Follow-Up')} value={formatters.value(totals)} />}
      colors={colors}
      commonTooltipLabel={t('Patients')}
      css={AnalyticsStyles.dashboardChartStyle}
      isLoading={isLoadingLocations || isLoadingPracticeData}
      labels={labels}
    >
      <Chart.Header
        actions={[
          {
            label: t('Show More'),
            omit: noLocationsHaveAclAccess,
            onClick: () => {
              navigate({ to: '/analytics/practice/main' });
            },
            trackingId: trackingIds.analytics.showMorePracticeAnalytics,
          },
        ]}
        leftElement={isDemoAccount ? <DemoChip /> : null}
        title={t('Hygiene Follow-Up')}
      />
      {!isLoadingPracticeData && (
        <>
          {!isDemoAccount && !hasDataSynced ? (
            <DataSyncInProgress miniView />
          ) : (
            <>
              <Chart.PieChart
                appearance={{
                  width: AnalyticsConstants.DASHBOARD_CHART_SIZE,
                  height: AnalyticsConstants.DASHBOARD_CHART_SIZE,
                  innerRadius: 55,
                  outerRadius: 95,
                }}
                data={{
                  centerMetric: formatters.value(totals),
                  centerMetricLabel: labels.total,
                  groups: [
                    {
                      name: 'rescheduledSameDay',
                      value: rescheduledSameDay,
                    },
                    {
                      name: 'unscheduled',
                      value: unscheduled,
                    },
                  ],
                }}
              />
              {!!totals && (
                <Chart.Legends
                  values={{
                    rescheduledSameDay: formatters.value(rescheduledSameDay),
                    unscheduled: formatters.value(unscheduled),
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </Chart>
  );
});

PracticeAnalyticsOnDashboard.displayName = 'PracticeAnalyticsOnDashboard';
